import type { CaseReason, TradeRole } from 'types/interfaces/thread';
import { MAX_IMAGES_PER_MESSAGE } from 'constants/messageImages';

/**
 * Pure gates for the Summon Admin wizard (Asana 1219952262403623, Decisions
 * 16 and 17). The component renders from these; the test pins them.
 */

export type WizardStep = 1 | 2 | 3;

export const DESCRIPTION_MAX = 2000;
/** Photos are optional for every reason (Eric, 2026-10-28); the cap is the message image cap. */
export const PHOTOS_MAX = MAX_IMAGES_PER_MESSAGE;

export interface StepTwoRequirements {
  /** Shipped, tracking, est. arrival, days since ship. */
  showShipFacts: boolean;
  /** The affected-cards checklist. */
  showCards: boolean;
  /** Pre-check every card when the checklist first shows. */
  cardsPreChecked: boolean;
  /** At least one card must be checked. */
  cardsRequired: boolean;
  /** Photo picker, optional, up to PHOTOS_MAX. */
  showPhotos: boolean;
  /** The cancel explanation paragraph. */
  showCancelNote: boolean;
}

export const stepTwoRequirements = (reason: CaseReason): StepTwoRequirements => {
  switch (reason) {
    case 'RECEIVER_NOT_ARRIVED':
      return { showShipFacts: true, showCards: true, cardsPreChecked: true, cardsRequired: true, showPhotos: false, showCancelNote: false };
    case 'RECEIVER_ARRIVED_INCORRECT':
      return { showShipFacts: false, showCards: true, cardsPreChecked: false, cardsRequired: true, showPhotos: true, showCancelNote: false };
    case 'SENDER_PARTNER_NOT_COMPLETED':
      return { showShipFacts: true, showCards: false, cardsPreChecked: false, cardsRequired: false, showPhotos: false, showCancelNote: false };
    case 'SENDER_CANCEL':
    case 'RECEIVER_CANCEL':
      return { showShipFacts: false, showCards: false, cardsPreChecked: false, cardsRequired: false, showPhotos: false, showCancelNote: true };
    default:
      return { showShipFacts: false, showCards: false, cardsPreChecked: false, cardsRequired: false, showPhotos: false, showCancelNote: false };
  }
};

export interface WizardState {
  reason: CaseReason | null;
  affectedItemIds: string[];
  photoCount: number;
  description: string;
}

/** Whether Continue (or Summon Admin on step 3) is enabled. Photos and the description are never required. */
export const isStepComplete = (step: WizardStep, state: WizardState): boolean => {
  if (!state.reason) return false;
  if (step === 1) return true;
  const req = stepTwoRequirements(state.reason);
  if (req.cardsRequired && state.affectedItemIds.length < 1) return false;
  if (state.photoCount > PHOTOS_MAX) return false;
  if (state.description.length > DESCRIPTION_MAX) return false;
  return true;
};

/** The initial affected-card selection when step 2 first shows for a reason. */
export const initialAffectedItemIds = (reason: CaseReason, allItemIds: string[]): string[] =>
  stepTwoRequirements(reason).cardsPreChecked ? [...allItemIds] : [];

export const roleWord = (role: TradeRole): 'from' | 'to' => (role === 'RECEIVER' ? 'from' : 'to');

export const daysSince = (iso: string | null | undefined, now: Date = new Date()): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((now.getTime() - t) / (24 * 60 * 60 * 1000)));
};
